import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Link,
  InputAdornment
} from '@mui/material';
import { useFormik } from 'formik';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  ForgotPasswordSchema,
  CodeSchema,
  RegistrationSchema
} from 'shared/schemas/authentication-schema';
import AuthContainerForm from 'shared/components/AuthContainerForm';
import ClockLoader from 'shared/components/ClockLoader';
import { useAuth } from 'shared/hooks/useAuth';
import {
  absoluteAssetUrlForStoredPath,
  fetchPublicTenantBrandingById,
  getCachedPublicBranding,
} from 'shared/api/services/publicTenantBrandingService';
import {
  buildTenantLoginPath,
  isTenantUuid,
  readRouteTenantId,
  resolveTenantPublicPathKey,
} from 'shared/utils/tenantRoute';
import { rememberTenantFromBranding } from 'shared/utils/lastTenantLogin';

const DEFAULT_BACKGROUND = '#F4F4F4';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const { tenantId: tenantIdParam } = useParams();
  const routeTenantId = readRouteTenantId(tenantIdParam);
  const [publicBranding, setPublicBranding] = useState(null);
  const [isBrandingLoading, setIsBrandingLoading] = useState(false);

  useEffect(() => {
    let active = true;

    if (!routeTenantId) {
      setPublicBranding(null);
      setIsBrandingLoading(false);
      return () => {
        active = false;
      };
    }

    const cached = getCachedPublicBranding(routeTenantId);
    if (cached) {
      setPublicBranding(cached);
      setIsBrandingLoading(false);
    } else {
      setIsBrandingLoading(true);
    }

    (async () => {
      const branding = await fetchPublicTenantBrandingById(routeTenantId);
      if (!active) return;
      if (branding) {
        setPublicBranding(branding);
        rememberTenantFromBranding(branding);
        const prettyKey = resolveTenantPublicPathKey({
          name: branding.name,
          id: branding.id,
          fallback: routeTenantId,
        });
        if (
          prettyKey &&
          isTenantUuid(routeTenantId) &&
          prettyKey.toLowerCase() !== String(routeTenantId).toLowerCase()
        ) {
          navigate(`/resetPassword/${encodeURIComponent(prettyKey)}`, { replace: true });
        }
      } else if (!cached) {
        setPublicBranding(null);
      }
      setIsBrandingLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [routeTenantId, navigate]);

  const pagePrimary = publicBranding?.primaryColor || '#1F2A44';
  const pageAccent = publicBranding?.secondaryColor || '#C23636';
  const pageBackground = publicBranding?.backgroundColor || DEFAULT_BACKGROUND;
  const tenantPhoto = typeof publicBranding?.tenantPhoto === 'string'
    ? publicBranding.tenantPhoto.trim()
    : '';
  const tenantLogoSrc = absoluteAssetUrlForStoredPath(tenantPhoto);
  const isPageLoading = Boolean(routeTenantId) && isBrandingLoading && !publicBranding;

  if (isPageLoading) {
    return (
      <AuthContainerForm
        backgroundColor={DEFAULT_BACKGROUND}
        minimal
      >
        <ClockLoader message="Loading tenant branding..." height="220px" />
      </AuthContainerForm>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {step === 1 && (
        <RequestResetPassword
          setStep={setStep}
          setEmail={setEmail}
          publicBranding={publicBranding}
          routeTenantId={routeTenantId}
          tenantLogoSrc={tenantLogoSrc}
        />
      )}
      {step === 2 && (
        <VerifyResetCode
          setStep={setStep}
          email={email}
          publicBranding={publicBranding}
          tenantLogoSrc={tenantLogoSrc}
        />
      )}
      {step === 3 && (
        <SetNewPassword
          setStep={setStep}
          email={email}
          publicBranding={publicBranding}
          tenantLogoSrc={tenantLogoSrc}
        />
      )}
    </Box>
  );
};


// ✅ Shared Logo Block (same as Login)

const LogoBlock = ({ publicBranding, tenantLogoSrc }) => {
  const tenantName = useMemo(() => {
    const name = publicBranding?.name;
    return typeof name === 'string' && name.trim() ? name.trim() : '';
  }, [publicBranding?.name]);

  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  useEffect(() => {
    setLogoLoadFailed(false);
  }, [publicBranding?.tenantPhoto]);

  const titleColor = publicBranding?.primaryColor || '#1F2A44';
  const shouldShowTenantLogo = Boolean(tenantLogoSrc) && !logoLoadFailed;

  return (
    <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 }, mx: 'auto', width: '100%', textAlign: 'center' }}>
      <Box sx={{ mb: 1.5 }}>
        {shouldShowTenantLogo ? (
          <Box
            component="img"
            src={tenantLogoSrc}
            alt={tenantName ? `${tenantName} logo` : 'Tenant logo'}
            onError={() => setLogoLoadFailed(true)}
            sx={{
              display: 'block',
              width: { xs: 'min(100%, 220px)', sm: 240, md: 260 },
              maxWidth: '100%',
              maxHeight: { xs: 88, sm: 96, md: 108 },
              mx: 'auto',
              objectFit: 'contain',
              borderRadius: 2,
            }}
          />
        ) : null}
      </Box>
      {tenantName ? (
        <Typography variant="h5" sx={{ fontWeight: 700, color: titleColor }}>
          {tenantName}
        </Typography>
      ) : null}
    </Box>
  );
};


// ✅ Step 1

const RequestResetPassword = ({
  setStep,
  setEmail,
  publicBranding,
  routeTenantId,
  tenantLogoSrc,
}) => {
  const { requestPasswordReset } = useAuth();
  const [loading, setLoading] = useState(false);
  const loginPath = buildTenantLoginPath(
    resolveTenantPublicPathKey({
      name: publicBranding?.name,
      id: publicBranding?.id,
      fallback: routeTenantId,
    })
  );
  const pagePrimary = publicBranding?.primaryColor || '#1F2A44';
  const pageAccent = publicBranding?.secondaryColor || '#C23636';
  const pageBackground = publicBranding?.backgroundColor || DEFAULT_BACKGROUND;

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: ForgotPasswordSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const result = await requestPasswordReset(values.email);
        if (result?.ok) {
          setEmail(values.email);
          setStep(2);
        }
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <AuthContainerForm
      backgroundColor={pageBackground}
      primaryColor={pagePrimary}
      secondaryColor={pageAccent}
    >
      <LogoBlock publicBranding={publicBranding} tenantLogoSrc={tenantLogoSrc} />

      <Typography variant="h5" mb={1}>
        Enter Email To Reset Password
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        A verification code will be emailed to you. It stays valid for 5 minutes.
      </Typography>

      <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
        <TextField
          fullWidth
          label="Email Address"
          {...formik.getFieldProps("email")}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          margin="normal"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: pagePrimary,
            '&:hover': {
              bgcolor: pagePrimary,
              filter: 'brightness(0.95)',
            },
          }}
        >
          {loading ? <ClockLoader inline size={20} /> : 'Send Reset Code'}
        </Button>
      </form>

      <Box mt={2}>
        <Typography>
          I remember my password?{' '}
          <Link component={RouterLink} to={loginPath}>
            back to login
          </Link>
        </Typography>
      </Box>
    </AuthContainerForm>
  );
};


// ✅ Step 2

const VerifyResetCode = ({ setStep, email, publicBranding, tenantLogoSrc }) => {
  const { verifyResetCode } = useAuth();
  const [loading, setLoading] = useState(false);
  const pagePrimary = publicBranding?.primaryColor || '#1F2A44';
  const pageAccent = publicBranding?.secondaryColor || '#C23636';
  const pageBackground = publicBranding?.backgroundColor || DEFAULT_BACKGROUND;

  const formik = useFormik({
    initialValues: { code: "" },
    validationSchema: CodeSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const result = await verifyResetCode(email, values.code);
        if (result?.ok) {
          setStep(3);
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <AuthContainerForm
      backgroundColor={pageBackground}
      primaryColor={pagePrimary}
      secondaryColor={pageAccent}
    >
      <LogoBlock publicBranding={publicBranding} tenantLogoSrc={tenantLogoSrc} />

      <Typography variant="h5" mb={1}>
        Enter Reset Code
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Check your email inbox and spam folder. The code expires in 5 minutes.
      </Typography>

      <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
        <TextField
          fullWidth
          label="Reset Code"
          {...formik.getFieldProps("code")}
          error={formik.touched.code && Boolean(formik.errors.code)}
          helperText={formik.touched.code && formik.errors.code}
          margin="normal"
        />

        <Button
          type="submit"
          fullWidth
          disabled={loading}
          sx={{
            bgcolor: pagePrimary,
            color: '#fff',
            '&:hover': {
              bgcolor: pagePrimary,
              filter: 'brightness(0.95)',
            },
          }}
        >
          {loading ? <ClockLoader inline size={20} /> : 'Verify Code'}
        </Button>
      </form>
    </AuthContainerForm>
  );
};


// ✅ Step 3
const SetNewPassword = ({ setStep, email, publicBranding, tenantLogoSrc }) => {
  const { resetPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const pagePrimary = publicBranding?.primaryColor || '#1F2A44';
  const pageAccent = publicBranding?.secondaryColor || '#C23636';
  const pageBackground = publicBranding?.backgroundColor || DEFAULT_BACKGROUND;

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: RegistrationSchema.pick(["password", "confirmPassword"]),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await resetPassword(email, values.password, values.confirmPassword);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <AuthContainerForm
      backgroundColor={pageBackground}
      primaryColor={pagePrimary}
      secondaryColor={pageAccent}
    >
      <LogoBlock publicBranding={publicBranding} tenantLogoSrc={tenantLogoSrc} />

      <Typography variant="h5" mb={2}>
        Set New Password
      </Typography>

      <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
        <TextField
          fullWidth
          label="New Password"
          type={showPassword ? "text" : "password"}
          {...formik.getFieldProps("password")}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          {...formik.getFieldProps("confirmPassword")}
          error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
          helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          margin="normal"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          disabled={loading}
          sx={{
            bgcolor: pagePrimary,
            color: '#fff',
            '&:hover': {
              bgcolor: pagePrimary,
              filter: 'brightness(0.95)',
            },
          }}
        >
          {loading ? <ClockLoader inline size={20} /> : 'Save New Password'}
        </Button>
      </form>
    </AuthContainerForm>
  );
};

export default ResetPassword;